// HU-25 · Expediente, sección «Disposiciones de la autoridad» (`ui/MG-ILE-53.md`, solo lectura
// más «Registrar disposición» con el caso fijado).
//
// DE DÓNDE SALEN. De `expediente.disposiciones[]` (`DisposicionDelExpedienteSalida`): TODAS las del
// caso, también las cumplidas, por orden y con los mismos nombres y textos que la fila de
// «Disposiciones y seguimiento» (§8.1 R8). Antes se pedía la lista de compromisos y se filtraba por
// el caso, y esa lista responde 403 a registrador, aprobador y administrador: la sección no les
// salía. Ahora la ve quien abre el expediente, sin pedir nada aparte.
//
// «Registrar disposición» se ofrece si `caso.acciones_permitidas` trae `registrar_disposicion` (AR
// o Representante del País de su ámbito, caso grave no cerrado), no por el rol de la sesión. Tras
// registrar, el expediente se relee entero (§8.1 R3): tabla, sustento con el oficio y condición C-08.

import { useMemo, useState } from 'react';
import { AlertTriangle, FilePlus2, Scale } from 'lucide-react';
import { Alert, Button, Card, DataTable, EmptyState, Mono, StatusBadge } from './ui';
import type { ColumnaDeTabla, FilaDeTabla } from './ui';
import RegistrarDisposicion from './RegistrarDisposicion';
import type { DisposicionRegistrada } from './RegistrarDisposicion';
import type { DisposicionDelExpediente } from '../api/expedientes';
import { comoFecha } from '../lib/fechas';
import { tonoDeLaClase, tonoDelCompromiso } from '../lib/etiquetas';

const COLUMNAS: ColumnaDeTabla[] = [
  { key: 'que', label: 'Qué dispuso', className: 'w-[34%]' },
  { key: 'clase', label: 'Tipo' },
  { key: 'responsable', label: 'Responsable' },
  { key: 'plazo', label: 'Plazo' },
  { key: 'estado', label: 'Estado' },
];

function celda(r: FilaDeTabla, { key }: ColumnaDeTabla) {
  const d = r as unknown as DisposicionDelExpediente;
  if (key === 'que') {
    return (
      <div>
        <p className="text-body text-ink">{d.descripcion}</p>
        <p className="text-meta text-ink-soft">{[d.autoridad, d.texto_de_registro].filter(Boolean).join(' · ')}</p>
      </div>
    );
  }
  if (key === 'clase') return <StatusBadge tone={tonoDeLaClase(d.tipo)}>{d.texto_de_clase}</StatusBadge>;
  if (key === 'responsable') return d.responsable;
  if (key === 'plazo') {
    return (
      <div>
        <Mono>{comoFecha(d.plazo)}</Mono>
        {d.texto_de_vencimiento && (
          <p className="text-meta text-rose-700 inline-flex items-center gap-1">
            <AlertTriangle size={12} aria-hidden="true" /> {d.texto_de_vencimiento}
          </p>
        )}
      </div>
    );
  }
  if (key === 'estado') return <StatusBadge tone={tonoDelCompromiso(d.estado)}>{d.texto_del_estado}</StatusBadge>;
  return null;
}

export default function DisposicionesDelCaso({ casoId, numero, autoridad, disposiciones, puedeRegistrar, onCambio }: {
  casoId: string;
  numero: string;
  autoridad: string | null;
  /** `expediente.disposiciones[]`, tal cual. */
  disposiciones: DisposicionDelExpediente[];
  /** `caso.acciones_permitidas` trae `registrar_disposicion`. */
  puedeRegistrar: boolean;
  /** Tras registrar: el expediente se refresca (tabla, sustento con el oficio, condición C-08). */
  onCambio?: () => void;
}) {
  const [registrando, setRegistrando] = useState(false);
  const [exito, setExito] = useState<DisposicionRegistrada | null>(null);
  // Estable: el modal reinicia su formulario cuando cambia el caso fijado.
  const casoFijo = useMemo(() => ({ casoId, numero, autoridad }), [casoId, numero, autoridad]);

  const alRegistrar = (r: DisposicionRegistrada) => {
    setRegistrando(false);
    setExito(r);
    onCambio?.();
  };

  return (
    <Card className="mt-4" title="Disposiciones de la autoridad" icon={Scale} pad={false}
      actions={puedeRegistrar ? (
        <Button size="sm" variant="secondary" icon={FilePlus2} onClick={() => setRegistrando(true)}
          data-testid="expediente-registrar-disposicion">
          Registrar disposición
        </Button>
      ) : undefined}>
      {exito && (
        <div className="px-5 pt-4">
          <Alert tone={exito.aviso ? 'warning' : 'success'} title="Disposición registrada" data-testid="expediente-exito"
            onCerrar={() => setExito(null)}>
            {exito.aviso}
          </Alert>
        </div>
      )}
      <DataTable columns={COLUMNAS} rows={disposiciones.map((d) => ({ ...d, id: d.disposicion_id }))} renderCell={celda} sinMarco
        data-testid="expediente-disposiciones" testidDeFila="expediente-disposicion-fila" regionDesplazable="Disposiciones de la autoridad"
        empty={<EmptyState title="La autoridad no ha emitido disposiciones" data-testid="expediente-disposiciones-vacio" />} />
      {puedeRegistrar && (
        <RegistrarDisposicion abierto={registrando} onCerrar={() => setRegistrando(false)} onRegistrada={alRegistrar}
          casoFijo={casoFijo} testidDelError="expediente-error-envio" />
      )}
    </Card>
  );
}
